import { useTheme } from '../hooks/useTheme';

/**
 * Two explicit options rather than one ambiguous switch: the current theme is
 * readable at a glance, and each control says what it does. Implemented as a
 * radio group so arrow keys work the way a segmented control should.
 */
export function ThemeToggle({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useTheme();

  const options = [
    { value: 'light' as const, label: 'Light', icon: <SunIcon /> },
    { value: 'dark' as const, label: 'Dark', icon: <MoonIcon /> },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={`border-line bg-surface-2 inline-flex items-center gap-0.5 rounded-full border p-1 ${className}`}
    >
      {options.map((option) => {
        const active = theme === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`${option.label} theme`}
            onClick={() => setTheme(option.value)}
            className={`inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-[0.6875rem] font-bold tracking-[0.1em] uppercase transition-colors duration-200 motion-reduce:transition-none ${
              active
                ? 'bg-brand text-white shadow-[0_4px_14px_-6px_var(--brand-shadow)]'
                : 'text-muted hover:text-text'
            }`}
          >
            {option.icon}
            <span className="hidden sm:inline">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="4.5" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
      </g>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.5 8.5 0 1 0 10.2 10.2Z" fill="currentColor" />
    </svg>
  );
}
